import {
  IconSoccerField,
  IconBuilding,
  IconSatellite,
  IconSearch,
  IconStarFilled,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import type { OwnershipType } from "@/types/domain";

type StatusType =
  | "sport_field"
  | "ownership"
  | "satellite_confidence"
  | "web_confidence"
  | "reviews";

interface StatusIndicatorProps {
  type: StatusType;
  hasSportField?: boolean | null;
  ownership?: OwnershipType;
  confidence?: number | null;
  rating?: number | null;
  reviewCount?: number | null;
  className?: string;
  onClick?: () => void;
}

type ColorKey = "green" | "red" | "amber" | "white";

const COLOR_STYLES: Record<ColorKey, React.CSSProperties> = {
  green: { backgroundColor: "#40C057", color: "white" },
  red: { backgroundColor: "#FF646C", color: "white" },
  amber: { backgroundColor: "#F59E0B", color: "white" },
  white: { backgroundColor: "white", color: "#374151", border: "1px solid #E5E7EB" },
};

function getSportFieldConfig(hasSportField: boolean | null) {
  if (hasSportField === true)
    return { color: "green" as const, label: "Has field" };
  if (hasSportField === false)
    return { color: "red" as const, label: "No field" };
  return { color: "amber" as const, label: "Unclassified" };
}

function getOwnershipConfig(ownership: OwnershipType) {
  switch (ownership) {
    case "public":
      return { color: "green" as const, label: "Public" };
    case "private":
    case "institutional":
      return {
        color: "red" as const,
        label: ownership === "private" ? "Private" : "Institutional",
      };
    default:
      return { color: "amber" as const, label: "Unknown" };
  }
}

function getConfidenceConfig(confidence: number | null) {
  if (confidence == null) return { color: "amber" as const, label: "N/A" };
  const pct = Math.round(confidence * 100);
  if (pct >= 80) return { color: "green" as const, label: `${pct}%` };
  if (pct >= 50) return { color: "amber" as const, label: `${pct}%` };
  return { color: "red" as const, label: `${pct}%` };
}

function getReviewsConfig(rating: number | null, reviewCount: number | null) {
  const label = rating != null ? `${rating} (${reviewCount ?? 0})` : "No reviews";
  return { color: "white" as const, label };
}

const iconMap = {
  sport_field: IconSoccerField,
  ownership: IconBuilding,
  satellite_confidence: IconSatellite,
  web_confidence: IconSearch,
  reviews: IconStarFilled,
} as const;

const titleMap = {
  sport_field: "Sport Field",
  ownership: "Ownership",
  satellite_confidence: "Satellite",
  web_confidence: "Web Research",
  reviews: "Reviews",
} as const;

export function StatusIndicator({
  type,
  hasSportField,
  ownership,
  confidence,
  rating,
  reviewCount,
  className,
  onClick,
}: StatusIndicatorProps) {
  let config: { color: ColorKey; label: string };

  switch (type) {
    case "sport_field":
      config = getSportFieldConfig(hasSportField ?? null);
      break;
    case "ownership":
      config = getOwnershipConfig(ownership ?? "unknown");
      break;
    case "satellite_confidence":
    case "web_confidence":
      config = getConfidenceConfig(confidence ?? null);
      break;
    case "reviews":
      config = getReviewsConfig(rating ?? null, reviewCount ?? null);
      break;
  }

  const Icon = iconMap[type];
  const title = titleMap[type];
  const isReviews = type === "reviews";

  const style = COLOR_STYLES[config.color];

  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      className={cn(
        "flex flex-col items-center justify-center gap-1 rounded-sm flex-1 min-w-0 aspect-square",
        onClick && "cursor-pointer hover:opacity-80 transition-opacity",
        className,
      )}
      style={style}
      onClick={onClick}
    >
      <Icon
        size={30}
        stroke={1.5}
        className="shrink-0"
        style={isReviews ? { color: "#F59E0B" } : undefined}
      />
      <span className="text-[10px] font-medium leading-tight truncate w-full text-center px-1">
        {title}
      </span>
      <span className="text-xs font-semibold leading-tight truncate w-full text-center px-1">
        {config.label}
      </span>
    </Wrapper>
  );
}
